import { type OperationId, operationIds } from "@gen/app-access-config";
import { CoreQuery } from "@gen/app-query";
import Button from "@mui/material/Button";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { ActionDialog } from "~/components/action_dialog";

interface AcceptF2fDepositActionProps {
  canExecuteForStatus: (operationId: OperationId) => boolean;
  userTransactionRole: "buyer" | "seller";
  isDepositInReview?: boolean;
  transactionId: number;
}

export function AcceptF2fDepositAction({
  canExecuteForStatus,
  userTransactionRole,
  isDepositInReview,
  transactionId,
}: AcceptF2fDepositActionProps) {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { handleSubmit } = useForm({ defaultValues: {} });

  const { mutate: acceptF2fDeposit, isPending: isAcceptingF2fDeposit } =
    CoreQuery.useAdmin_acceptF2fDepositMutationWithParameters({
      meta: {
        success: `F2F deposit accepted for ${userTransactionRole}`,
      },
      onSuccess: () => {
        if (transactionId) {
          queryClient.invalidateQueries({
            queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
          });
        }
        setDialogOpen(false);
      },
    });

  if (
    !canExecuteForStatus(operationIds["core_admin.acceptF2fDeposit"]) &&
    !isDepositInReview
  ) {
    return null;
  }

  const onSubmit = () => {
    acceptF2fDeposit({
      transaction_id: transactionId,
      acceptF2fDepositBody: {},
    });
  };

  return (
    <>
      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={() => setDialogOpen(true)}
        data-testid={`accept-deposit-${userTransactionRole}-button`}
      >
        Accept deposit for{" "}
        {userTransactionRole[0].toUpperCase() + userTransactionRole.slice(1)}
      </Button>
      {isDialogOpen && (
        <ActionDialog
          heading="Accept F2F deposit"
          message={`Are you sure you want to accept F2F deposit for the ${userTransactionRole}?`}
          confirmButtonMessage="Yes, accept deposit"
          cancelButtonMessage="Cancel"
          isConfirming={isAcceptingF2fDeposit}
          confirm={handleSubmit(onSubmit)}
          cancel={() => setDialogOpen(false)}
          dataTestId={`accept-deposit-${userTransactionRole}`}
        />
      )}
    </>
  );
}
